<template>
  <div>
    <div v-show="loading" class="text-center">
      <v-progress-circular style="width: 64px;height:64px;"
                           indeterminate size="64"
      />
    </div>

    <div v-show="!loading">
      <AppCard class="rounded-lg archives-list-card">
        <div class="d-flex flex-column">
          <div class="pl-4 py-3 results-count-container">
            <span v-if="itemCount !== 0" class="heading-xs-bold text-text-primary py-1 px-2">
              {{ $t('archives.ArchivesList.itemCount', itemCount) }}
            </span>
          </div>
        </div>


        <v-data-table-server v-model="selectedItems"
                             :items="items"
                             :items-length="itemCount"
                             must-sort
                             :headers="columns"
                             :sort-by="sort"
                             :show-select="true"
                             :return-object="true"
                             :value-comparator="comparatorFunction"
                             @update:sort-by="onSortChange"
        >
          <template #item.docName="{ item }">
            <AppTooltip location="bottom" max-width="300">
              <template v-slot:activator="{ props }">
                <div class="d-flex align-center" v-bind="props">
                  <ClosdFildersIcon class="mr-2"
                                    :document="item"
                                    :signedFile="item.isSigned == true"
                  />
                  <span class="text-truncated">{{ item.docName }}</span>
                </div>
              </template>
              <span>{{ item.docName }}</span>
            </AppTooltip>
          </template>

          <template #item.archiveDate="{ item }">
            <DateDisplay :value="item.archiveDate" />
          </template>

          <template #item.archiverName="{ item }">
            <ArchivesListArchiverName :name="item.archiverName" />
          </template>

          <template #item.signedDocument="{ item }">
            <BooleanDisplay :value="item.isSigned == true" />
          </template>

          <template #item.signedDate="{ item }">
            <DateDisplay :value="item.signedDate" />
          </template>

          <template #item._options="{item, index}">
            <div style="min-width: 75px">
              <AppButton v-if="isArchivingAdmin || item.canDownload"
                         icon
                         density="comfortable"
                         @click="downloadFile(item)"
              >
                <app-material-icon name="download" />
              </AppButton>
              <AppMenu :menu="getMenuForItem(item)"
                       min-width="250px"
                       @update:return-value="indexMenuOpen = null"
              >
                <template #activator="{ props }">
                  <AppButton icon
                             density="comfortable"
                             v-bind="props"
                             @click="toggleMenu(index)"
                  >
                    <app-material-icon name="more_horiz" />
                  </AppButton>
                </template>
              </AppMenu>
            </div>
          </template>

          <template #[`header.docName`]="{ column, toggleSort }">
            <div class="clickable d-flex align-center text-text-primary" @click.stop="toggleSort(column)">
              <span>{{ column.title }}</span>
              <app-material-icon
                v-if="sort[0]?.key !== 'docName'"
                name="mobiledata_arrows"
                color="icon-secondary"
              />
              <app-material-icon
                v-else-if="sort[0]?.order === 'asc'"
                name="arrow_downward"
              />
              <app-material-icon
                v-else
                name="arrow_upward"
              />
            </div>
          </template>
          <template #[`header.cmRef`]="{ column, toggleSort }">
            <div class="clickable d-flex align-center text-text-primary" @click.stop="toggleSort(column)">
              <span>{{ column.title }}</span>
              <app-material-icon
                v-if="sort[0]?.key !== 'cmRef'"
                name="mobiledata_arrows"
                 color="icon-secondary"
              />
              <app-material-icon
                v-else-if="sort[0]?.order === 'asc'"
                name="arrow_downward"
              />
              <app-material-icon
                v-else
                name="arrow_upward"
              />
            </div>
          </template>
          <template #[`header.archiveDate`]="{ column, toggleSort }">
            <div class="clickable d-flex align-center text-text-primary" @click.stop="toggleSort(column)">
              <span>{{ column.title }}</span>
              <app-material-icon
                v-if="sort[0]?.key !== 'archiveDate'"
                name="mobiledata_arrows"
                 color="icon-secondary"
              />
              <app-material-icon
                v-else-if="sort[0]?.order === 'asc'"
                name="arrow_downward"
              />
              <app-material-icon
                v-else
                name="arrow_upward"
              />
            </div>
          </template>
          <template #[`header.archiverName`]="{ column, toggleSort }">
            <div class="clickable d-flex align-center text-text-primary" @click.stop="toggleSort(column)">
              <span>{{ column.title }}</span>
              <app-material-icon
                v-if="sort[0]?.key !== 'archiverName'"
                name="mobiledata_arrows"
                 color="icon-secondary"
              />
              <app-material-icon
                v-else-if="sort[0]?.order === 'asc'"
                name="arrow_downward"
              />
              <app-material-icon
                v-else
                name="arrow_upward"
              />
            </div>
          </template>
          <template #[`header.signedDate`]="{ column, toggleSort }">
            <div class="clickable d-flex align-center text-text-primary" @click.stop="toggleSort(column)">
              <span>{{ column.title }}</span>
              <app-material-icon
                v-if="sort[0]?.key !== 'signedDate'"
                name="mobiledata_arrows"
                 color="icon-secondary"
              />
              <app-material-icon
                v-else-if="sort[0]?.order === 'asc'"
                name="arrow_downward"
              />
              <app-material-icon
                v-else
                name="arrow_upward"
              />
            </div>
          </template>
          <template #bottom>
            <div class="d-flex justify-center ma-4">
              <AppPagination :current-page="page"
                             :page-size="ARCHIVES_LIST_PAGE_SIZE"
                             :total="itemCount"
                             hide-page-size
                             @update:current-page="onPageChange"
              />
            </div>
          </template>
        </v-data-table-server>
      </AppCard>
    </div>

    <AppDownloadFile ref="appDownloadFileRef" :link="downloadLink" />

    <ArchivesListDetailsDialog v-if="currentItemDetailsOpen"
                               :archive="currentItemDetails"
                               :fields="modalFields"
                               @action="handleDetailsModalAction"
                               @close="currentItemDetailsOpen = false"
    />

    <ArchivesListShareDialog v-if="shareDialogOpen"
                             :data="currentShareItems"
                             @close="shareDialogOpen = false"
    />

    <ArchivesListSendByEmailDialog v-if="archiveToBeSendByEmail"
                                   :archive-id="archiveToBeSendByEmail.id"
                                   @close="archiveToBeSendByEmail = null"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import BooleanDisplay from '@/common/BooleanDisplay.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { useArchivesStore } from '@/stores/archivesStore'

import ArchivesListArchiverName from './ArchivesListArchiverName.vue'
import ArchivesListDetailsDialog from './dialogs/ArchivesListDetailsDialog.vue'
import ArchivesListSendByEmailDialog from './dialogs/ArchivesListSendByEmailDialog.vue'
import ArchivesListShareDialog from './dialogs/ArchivesListShareDialog.vue'

const ARCHIVES_LIST_PAGE_SIZE = 25

export default {
  name: 'ArchivesList',
  components: {
    AppCard,
    AppButton,
    AppDownloadFile,
    AppMenu,
    AppPagination,
    AppTooltip,
    ArchivesListArchiverName,
    ArchivesListDetailsDialog,
    ArchivesListSendByEmailDialog,
    ArchivesListShareDialog,
    BooleanDisplay,
    ClosdFildersIcon,
    DateDisplay,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      required: true,
    },
    itemCount: {
      type: Number,
      required: true,
    },
    additionalFields: {
      type: Array,
      default: () => [],
    },
    sort: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['page-change', 'sort-change'],
  data () {
    return {
      ARCHIVES_LIST_PAGE_SIZE,
      indexMenuOpen: null,
      currentItemDetails: null,
      currentItemDetailsOpen: false,
      currentShareItems: [],
      shareDialogOpen: false,
      downloadLink: '',
      archiveToBeSendByEmail: null,
    }
  },
  computed: {
    ...mapGetters('user', ['isArchivingAdmin']),
    selectedDocuments () {
      return this.archivesStore.selectedDocuments
    },
    selectedItems: {
      get () {
        return this.selectedDocuments
      },
      set (val) {
        this.archivesStore.selectDocuments(val)
      },
    },
    computedAdditionalFields () {
      return (this.additionalFields ?? []).map(metadata => ({
        value: metadata.field,
        title: metadata[this.$i18n.locale],
        sortable: true,
      }))
    },
    columns () {
      const fields = [
        {
          value: 'docName',
          title: this.$t('archives.ArchivesList.nameField'),
          sortable: true,
        },
        {
          value: 'cmRef',
          title: this.$t('archives.ArchivesList.folderRefField'),
          sortable: true,
        },
        {
          value: 'archiveDate',
          title: this.$t('archives.ArchivesList.archiveDateField'),
          sortable: true,
        },
        {
          value: 'archiverName',
          title: this.$t('archives.ArchivesList.archiverNameField'),
          sortable: true,
        },
        {
          value: 'signedDocument',
          title: this.$t('archives.ArchivesList.signedDocumentField'),
        },
        {
          value: 'signedDate',
          title: this.$t('archives.ArchivesList.signDateField'),
          sortable: true,
        },
      ]

      fields.push(...this.computedAdditionalFields)

      fields.push(
        {
          value: '_options',
          title: this.$t('archives.ArchivesList.optionsField'),
          sortable: false,
        },
      )

      return fields
    },
    modalFields () {
      return [
        {
          value: 'cmRef',
          title: this.$t('archives.ArchivesList.folderRefField'),
        },
        {
          value: 'signersNames',
          title: this.$t('archives.ArchivesList.signersNamesField'),
        },
        {
          value: 'archiverName',
          title: this.$t('archives.ArchivesList.archiverNameField'),
        },
        {
          value: 'signedDate',
          title: this.$t('archives.ArchivesList.signDateField'),
        },
        {
          value: 'docExt',
          title: this.$t('archives.ArchivesList.documentFormatField'),
        },
        {
          value: 'docSize',
          title: this.$t('archives.ArchivesList.documentSizeField'),
        },
        {
          value: 'signedDocument',
          title: this.$t('archives.ArchivesList.signedDocumentField'),
        },
        {
          value: 'archiveDate',
          title: this.$t('archives.ArchivesList.archiveDateField'),
        },
        {
          value: 'archiveEndDate',
          title: this.$t('archives.ArchivesList.archiveEndDateField'),
        },
        ...this.computedAdditionalFields,
      ]
    },
  },
  methods: {
    async downloadCertificate (item) {
      this.downloadLink = `${axios.defaults.baseURL}/downloads/downloadArchiveCert/${item.id}`
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
    },
    async downloadFile(item) {
      this.downloadLink = `${axios.defaults.baseURL}/downloads/downloadArchiveDoc/${item.id}`
      await this.$nextTick()
      this.$refs.appDownloadFileRef.submitForm()
    },
    getMenuForItem (item) {
      const menuItems = [
        {
          title: this.$t('archives.ArchivesList.propertiesOption'),
          action: this.showCurrentDetails,
        },
      ]

      if (this.isArchivingAdmin) {
        menuItems.unshift(
          {
            title: this.$t('archives.ArchivesList.shareOption'),
            action: () => this.shareCurrent(item),
          },
        )
      }

      if (item.hasCertificate) {
        menuItems.push({
          title: this.$t('archives.ArchivesList.certificateOption'),
          action: () => this.downloadCertificate(item),
        })
      }

      menuItems.push({
        title: this.$t('archives.ArchivesList.sendByEmail'),
        action: () => {
          this.archiveToBeSendByEmail = item
        },
      })

      return menuItems
    },
    handleDetailsModalAction (actionName) {
      switch (actionName) {
        case 'download':
          this.downloadFile(this.currentItemDetails)
          break
        case 'share':
          this.shareCurrent(this.currentItemDetails)
          break
        default:
          break
      }
    },
    shareCurrent (item) {
      this.currentShareItems = [item]
      this.shareDialogOpen = true
    },
    showCurrentDetails () {
      this.currentItemDetails = this.items[this.indexMenuOpen]
      this.currentItemDetailsOpen = true
    },
    toggleMenu (rowIndex) {
      if (this.indexMenuOpen !== rowIndex) {
        this.indexMenuOpen = rowIndex
      } else {
        // Reset
        this.indexMenuOpen = null
      }
    },
    onPageChange (page) {
      this.$emit('page-change', page)
    },
    onSortChange (sort) {
      this.$emit('sort-change', sort)
    },
    comparatorFunction (a, b) {
      return a.id === b.id
    },
  },
}
</script>
<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/typography-tokens";

.archives-list-card {
  padding: 0;

  .results-count-container {
    border-bottom: get-border();
  }
}

// The table typography is applied here and not in the template: part of the
// headers and all the cells are rendered by `v-data-table-server` itself, and
// its own `$table-font-size` would otherwise win over the tokens.
:deep(.v-table .v-table__wrapper > table > thead > tr > th) {
  @include text('heading-xs-bold');
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td) {
  @include text('body-md-regular');
  border-bottom: get-border() !important;
}

:deep(.v-table tbody) {
  color: rgb(var(--v-theme-text-secondary));
}
</style>
